import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Buttons, FlexContainer, Inputs, Text } from '@taxdown/piccolo-web';
import EventService from 'Services/events';
import { locizeNS } from 'Services/i18n';
import { setUserInfoAction } from 'Store/modules/auth/actions';
import { phoneSelector } from 'Store/modules/auth/selectors';
import { setIsLoadingAction, setIsNotLoadingAction } from 'Store/modules/app/actions';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import ContentContainer from 'Components/UI/ContentContainer';
import FormContainer from 'Components/UI/FormContainer';
import TwoColLayout from 'Components/Layouts/TwoColLayout';
import ContentBottom from 'Components/UI/ContentBottom';
import { routes, routesByStage } from 'Constants/routes';
import { stages } from 'Constants/stages';
import { useFetchSaveUserphone } from './hooks';

const RequestPhone = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.DG);
  const dispatch = useDispatch();
  const [phone, setPhone] = useState<string>();
  const [hasError, setHasError] = useState(false);
  const isPreviousYears = useSelector(routeStateSelector);
  const userStage = useSelector(userStageSelector);
  const route =
    userStage && userStage !== stages.DATA_RETRIEVAL
      ? routesByStage[userStage]
      : routes.DATAGATHERING_QUIZ;
  const { mutate, isLoading } = useFetchSaveUserphone({
    route,
    onHideModal: () => dispatch(setIsNotLoadingAction()),
    isPreviousYears,
  });
  const phoneSaved = useSelector(phoneSelector);

  const handleSubmit = (): void => {
    if (phone) {
      EventService.formSubmitted({
        screenId: 'RequestPhone',
        values: phone,
      });
      mutate(phone);
      dispatch(setUserInfoAction({ user: { phone } }));
    }
  };

  useEffect(() => {
    if (isLoading) {
      dispatch(setIsLoadingAction());
    }
  }, [isLoading]);

  useEffect(() => {
    if (phoneSaved) {
      setPhone(phoneSaved);
      setHasError(false);
    }
  }, []);

  return (
    <TwoColLayout>
      <ContentContainer>
        <FormContainer>
          <FlexContainer direction="column" gap="dimension_24">
            <Text type="headline" color="dark" size="XS_L_S">
              {t('RequestPhone.title')}
            </Text>
            <Text type="paragraphs" color="dark" size="S_M_S">
              {t('RequestPhone.subtitle')}
            </Text>
            <Inputs.Phone
              value={phone}
              label={t('RequestPhone.phoneInput.label')}
              placeholder="(1) 483 1212891"
              defaultCountry="MX"
              onChange={(value, error) => {
                setPhone(value);
                setHasError(error);
              }}
              id="phoneInput"
              hasError={hasError}
              description={t('RequestPhone.phoneInput.description')}
              errorMsg={t('RequestPhone.Input.error')}
            />
          </FlexContainer>
        </FormContainer>
        <ContentBottom>
          <Buttons.Classic
            size="m"
            mode="primary"
            disabled={hasError || !phone}
            testID="dgContinueButton"
            onClick={handleSubmit}
            label={t('RequestPhone.button.continue')}
          />
        </ContentBottom>
      </ContentContainer>
    </TwoColLayout>
  );
};

export default RequestPhone;
